import { stageUndoKind, type PipelineStage, type StageUndoPayload } from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { DealUndoFields, type DealActionMode } from '@/components/commercial/DealActionDialog';
import {
  emptyUndoForm,
  multipleLiveDealsNotice,
  refundInstant,
  undoErrorMessage,
} from '@/components/commercial/undoForm';
import { NextStepFields } from '@/components/leads/NextStepFields';
import { emptyNextStep, toNextStep, type NextStepForm } from '@/components/leads/nextStep';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast-context';
import { STAGE_LABEL } from '@/lib/format';
import { useCatalogs, useUndoStage } from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';

const TITLE: Record<string, string> = {
  CANCEL_APPOINTMENT: 'Cancelar la cita',
  ANNUL_QUOTE: 'Anular la proforma',
  REFUND_DEAL: 'Devolver o anular el proceso',
};

/**
 * Retroceder en el kanban: anula el hecho que sostiene la etapa actual (ADR-011). `canAnnul` = quien gestiona
 * (solo él anula procesos; devolver también puede el asesor dueño). `desist` = «Devolver y desistir» desde una
 * etapa con proceso vivo: la devolución sale con «Desistir» como siguiente paso (Lote 5 · H1). `liveDealCount`
 * (cuando quien abre lo sabe, como la ficha) = procesos vivos del lead: con más de uno, «Desistir» no cabe como
 * siguiente paso (quedaría otro proceso vivo y la API revertiría todo) y el diálogo lo avisa.
 */
export function StageUndoDialog({
  leadId,
  companyId,
  currentStage,
  open,
  canAnnul = true,
  desist = false,
  liveDealCount = 1,
  onOpenChange,
}: {
  leadId: string;
  companyId: string;
  currentStage: PipelineStage;
  open: boolean;
  canAnnul?: boolean;
  desist?: boolean;
  liveDealCount?: number;
  onOpenChange: (o: boolean) => void;
}) {
  const kind = stageUndoKind(currentStage);
  const { data: catalogs } = useCatalogs(companyId);
  const tz = useCompanyTimezone(companyId);
  const mutation = useUndoStage(leadId);
  const toast = useToast();
  const [mode, setMode] = useState<DealActionMode>('refund');
  const [form, setForm] = useState(() => emptyUndoForm(tz));
  const [reason, setReason] = useState('');
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  const [error, setError] = useState<string | null>(null);
  const allowDesist = liveDealCount <= 1;
  useEffect(() => {
    if (open) {
      setMode('refund');
      setForm(emptyUndoForm(tz));
      setReason('');
      setNext({ ...emptyNextStep(tz), kind: desist && allowDesist ? 'DESIST' : 'TASK' });
      setError(null);
    }
  }, [open, desist, allowDesist, tz]);
  if (!kind) return null;
  const refundAndDesist = desist && allowDesist && kind === 'REFUND_DEAL';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    let payload: StageUndoPayload;
    if (kind === 'CANCEL_APPOINTMENT')
      payload = {
        action: 'CANCEL_APPOINTMENT',
        reason: reason || null,
        next: toNextStep(next, tz),
      };
    else if (kind === 'ANNUL_QUOTE')
      payload = { action: 'ANNUL_QUOTE', reason, next: toNextStep(next, tz) };
    else if (mode === 'refund')
      payload = {
        action: 'REFUND_DEAL',
        refundedAt: refundInstant(form.refundedAt, tz),
        dropReason: form.dropReason,
        notes: form.notes || null,
        next: toNextStep(next, tz),
      };
    else
      payload = {
        action: 'ANNUL_DEAL',
        reason: form.reason,
        // Solo el motivo técnico de este modo; el de la devolución no viaja (P26).
        dropReason: form.techReason || null,
        next: toNextStep(next, tz),
      };
    try {
      const lead = await mutation.mutateAsync(payload);
      // «y lead desistido» solo si la API lo confirma (el desistimiento corre en la misma transacción).
      toast.success(
        lead.hasDesisted
          ? `Proceso ${mode === 'annul' && kind === 'REFUND_DEAL' ? 'anulado' : 'devuelto'} y lead desistido`
          : `Lead retrocedió a ${STAGE_LABEL[lead.pipelineStage]}`,
      );
      onOpenChange(false);
    } catch (err) {
      setError(undoErrorMessage(err, 'No se pudo retroceder'));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {refundAndDesist
                ? 'Devolver y desistir'
                : `Retroceder desde ${STAGE_LABEL[currentStage]}`}
            </DialogTitle>
            <DialogDescription>
              {refundAndDesist
                ? 'Un lead con separación o venta viva no se desiste directamente: primero se registra la devolución (la caída queda contada y la unidad vuelve al stock) y en el mismo paso se desiste el lead.'
                : `${TITLE[kind]}: en el kanban no se "pinta" la etapa, se anula el hecho que la sostiene y la etapa resultante se recalcula.`}
            </DialogDescription>
          </DialogHeader>
          {kind === 'CANCEL_APPOINTMENT' && (
            <div className="space-y-1">
              <Label htmlFor="undo-reason">Motivo (opcional)</Label>
              <Input
                id="undo-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej. el cliente no podrá asistir"
              />
            </div>
          )}
          {kind === 'ANNUL_QUOTE' && (
            <div className="space-y-1">
              <Label htmlFor="undo-reason">Motivo de anulación</Label>
              <Input
                id="undo-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={2}
                placeholder="Ej. precio equivocado"
              />
            </div>
          )}
          {kind === 'REFUND_DEAL' && (
            <DealUndoFields
              mode={mode}
              onModeChange={canAnnul ? setMode : undefined}
              value={form}
              onChange={setForm}
              dropReasons={catalogs?.dropReasons ?? []}
              idPrefix="undo"
              tz={tz}
            />
          )}
          {kind === 'REFUND_DEAL' && !allowDesist && (
            <p
              className="border-warning/40 bg-warning/10 rounded-md border px-3 py-2 text-sm"
              data-testid="undo-multiple-live"
            >
              {multipleLiveDealsNotice(liveDealCount)}
            </p>
          )}
          <NextStepFields
            value={next}
            onChange={setNext}
            desistReasons={catalogs?.desistReasons ?? []}
            idPrefix="undo-next"
            allowDesist={allowDesist}
          />
          {error && (
            <p
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="undo-submit">
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}{' '}
              {next.kind === 'DESIST' && kind === 'REFUND_DEAL'
                ? mode === 'refund'
                  ? 'Devolver y desistir'
                  : 'Anular y desistir'
                : 'Retroceder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
